import { ContentDrawer, AddIcon, UserItem, NewException } from '@halo/ui-chat-react';
import { Stack, Box, RadioGroup, Flex, Center, Text } from '@chakra-ui/react';
import {
  DisallowSendCategory,
  type GroupDisallowSendInfo,
  type GroupMember,
} from '@halo/chat-sdk';
import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const MemberNotifications = ({
  title,
  desc,
  trigger,
  value,
  onChange,
  showException = false,
  members,
  groupDisallowSendInfo,
  options,
  onAddException,
}: {
  title: string;
  desc?: string;
  trigger: React.ReactNode;
  value?: string;
  onChange?: (value: string) => void;
  showException?: boolean;
  members?: GroupMember[];
  groupDisallowSendInfo?: GroupDisallowSendInfo | null;
  options: {
    label: string;
    value: string;
  }[];
  onAddException?: () => void;
}) => {
  const { t } = useTranslation();
  const [selectedMember, setSelectedMember] = useState<GroupMember | null>(null);
  const [showNewException, setShowNewException] = useState(false);

  const handleMemberClick = (member: GroupMember) => {
    setSelectedMember(member);
    setShowNewException(true);
  };

  const handleCloseException = () => {
    setShowNewException(false);
    setSelectedMember(null);
  };

  const handleConfirmException = useCallback((_member: GroupMember) => {
    setShowNewException(false);
    setSelectedMember(null);
  }, []);

  const handleDeleteException = useCallback((_member: GroupMember) => {
    setShowNewException(false);
    setSelectedMember(null);
  }, []);

  const mergedOptions = useMemo(() => {
    return options;
  }, [options]);

  const showMembers = useMemo(() => {
    return members || [];
  }, [members]);

  const disallowSendList = useMemo(() => {
    return groupDisallowSendInfo?.settings_val === DisallowSendCategory.Whitelist
      ? groupDisallowSendInfo?.whitelist
      : groupDisallowSendInfo?.blacklist;
  }, [groupDisallowSendInfo]);

  return (
    <>
      <ContentDrawer title={title} trigger={<div>{trigger}</div>}>
        {desc && (
          <Box py="2.5" fontSize="xs" fontWeight="medium" color="fg.muted">
            {desc}
          </Box>
        )}
        <Box bg="bg" rounded="xl" mb="2">
          <RadioGroup.Root
            value={value}
            defaultValue="1"
            onValueChange={(details) => {
              if (details.value) {
                onChange?.(details.value);
              }
            }}
          >
            <Stack gap="0">
              {mergedOptions.map((item, index) => (
                <Box key={item.value} position="relative">
                  {index !== mergedOptions.length - 1 && (
                    <Box
                      position="absolute"
                      bottom="0"
                      left="2"
                      right="2"
                      height="1px"
                      bg="border.subtle"
                    />
                  )}
                  <RadioGroup.Item value={item.value} px="2" py="3.5" cursor="pointer">
                    <RadioGroup.ItemHiddenInput />
                    <RadioGroup.ItemIndicator />
                    <RadioGroup.ItemText fontWeight="semibold" fontSize="sm">
                      {item.label}
                    </RadioGroup.ItemText>
                  </RadioGroup.Item>
                </Box>
              ))}
            </Stack>
          </RadioGroup.Root>
        </Box>
        {showException && (
          <>
            <Box py="2.5" fontSize="xs" fontWeight="medium" color="fg.muted">
              {t('chatSettings.exception')}
            </Box>
            <Box bg="bg" rounded="xl" p="2">
              <Flex alignItems="center" gap="2.5" cursor="pointer" onClick={onAddException}>
                <Center fontSize="9xl">
                  <AddIcon />
                </Center>
                <Box flex="1" py="2.5" borderBottom="1px solid" borderColor="border.subtle">
                  <Text fontSize="sm" fontWeight="mediumBold">
                    {t('chatSettings.addException')}
                  </Text>
                </Box>
              </Flex>
              <Box>
                {/* TODO: member type*/}
                {disallowSendList?.map((member: any, index) => (
                  <UserItem
                    key={member.user_id}
                    {...member}
                    rippleEffect={false}
                    hoverable={true}
                    onClick={() => handleMemberClick(member)}
                    cursor="pointer"
                    borderColor={
                      index === showMembers.length - 1 ? 'transparent' : 'border.subtle'
                    }
                  />
                ))}
              </Box>
            </Box>
            <Text py="1" fontSize="xs" fontWeight="medium" color="fg.muted">
              {value === '0'
                ? t('chatSettings.setAMuteExceptionList')
                : value === '1'
                  ? t('chatSettings.setAnExceptionListForPostingPermissions')
                  : null}
            </Text>
            <NewException
              member={selectedMember}
              open={showNewException}
              onClose={handleCloseException}
              onConfirm={handleConfirmException}
              onDeleteClick={handleDeleteException}
            />
          </>
        )}
      </ContentDrawer>
    </>
  );
};

const MemoizedMemberNotifications = memo(MemberNotifications);
export { MemoizedMemberNotifications as MemberNotifications };
